import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/providers/auth-provider";
import { callOnboarding } from "@/services/player/onboarding";
import { Alert } from "@/components/alert/alert";

export type OnboardingStep =
  | "welcome"
  | "intro"
  | "username_input"
  | "country_picker"
  | "celebi_welcome_back"
  | "celebi_gift_intro"
  | "starter_picker"
  | "starter_reveal"
  | "camps_explanation"
  | "camp_picker"
  | "finished";

export interface OnboardingData {
  country?: string;
  username?: string;
  starterType?: string;
  starterTypes?: string[];
  starterName?: string;
  starters?: Array<{
    id: number;
    display_id: string;
    base_pokemon_name: string;
    level: number;
    active_skills: string[];
  }>;
  activeSkills?: string[] | undefined;
  campId?: string;
  campSelectionType?: "existing" | "recommended";
  alphaPermission?: boolean;
}

export const useOnboarding = () => {
  const { getAccessToken } = useAuth();
  const [step, setStep] = useState<OnboardingStep>("welcome");
  const [data, setData] = useState<OnboardingData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const steps: OnboardingStep[] = [
    "welcome",
    "intro",
    "username_input",
    "country_picker",
    "celebi_welcome_back",
    "celebi_gift_intro",
    "starter_picker",
    "starter_reveal",
    "camps_explanation",
    "camp_picker",
    "finished",
  ];

  const checkStatus = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;

    setIsLoading(true);
    setError(null);
    try {
      const res = await callOnboarding(token, {
        action: "get_status",
      });

      if (res.ok && res.data) {
        const { onboarding_completed, alpha_permission } = res.data;

        setData((prev) => ({ ...prev, alphaPermission: !!alpha_permission }));

        if (onboarding_completed) {
          setStep("finished");
        } else {
          setStep("welcome");
        }
      }
    } catch (e) {
      console.error("Error checking onboarding status:", e);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const nextStep = useCallback(() => {
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      const nextStepValue = steps[currentIndex + 1];
      if (nextStepValue) {
        setStep(nextStepValue);
      }
    }
  }, [step]);

  const updateData = useCallback((newData: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const verifyUsername = useCallback(
    async (username: string): Promise<boolean> => {
      try {
        const res = await callOnboarding(getAccessToken()!, {
          action: "verify_username",
          username,
        });
        return !!res.data?.available;
      } catch (e) {
        console.error("Error verifying username:", e);
        return false;
      }
    },
    [getAccessToken],
  );

  const createPlayer = useCallback(
    async (username: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await callOnboarding(getAccessToken()!, {
          action: "create_player",
          username: username,
          country: data.country,
        });
        if (!res.ok) {
          setError(res.message || "Failed to create player");
          return false;
        }
        return true;
      } catch (e) {
        setError("Network error creating player");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, data.country],
  );

  const updateCountry = useCallback(
    async (country: string) => {
      setIsLoading(true);
      try {
        const res = await callOnboarding(getAccessToken()!, {
          action: "update_country",
          country,
        });
        if (!res.ok) {
          setError(res.message || "Failed to update country");
        }
      } catch (e) {
        setError("Network error updating country");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken],
  );

  const pickStarter = useCallback(
    async (types: string[]) => {
      setIsLoading(true);
      try {
        const res = await callOnboarding(getAccessToken()!, {
          action: "pick_starter",
          starter_types: types,
        });

        const starters = res.data?.starters;
        if (!starters || starters.length === 0) {
          Alert("Failed to pick starters, please try again");
          return;
        }

        updateData({
          starters: starters,
          // Maintain compatibility if needed elsewhere
          starterName: starters[0].base_pokemon_name,
          activeSkills: starters[0].active_skills,
        });
      } catch (e) {
        Alert("Network error picking starter");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, updateData],
  );

  const completeOnboarding = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await callOnboarding(getAccessToken()!, {
        action: "complete",
      });
      if (res.ok) {
        setStep("finished");
      } else {
        setError(res.message || "Failed to complete onboarding");
      }
    } catch (e) {
      setError("Network error completing onboarding");
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  // No auto-advance after starter is picked, view handles sequential reveal
  return {
    step,
    data,
    isLoading,
    error,
    nextStep,
    updateData,
    pickStarter,
    createPlayer,
    verifyUsername,
    updateCountry,
    completeOnboarding,
    checkStatus,
  };
};
